/**
 * EditableDataRow
 * A compact, DataRow-shaped (see components/layout/DataRow.tsx) read-only/edit-toggle
 * field. Renders read-only text with an inline pencil-icon toggle by default; the toggle
 * swaps the value for an input/select with inline Confirm/Cancel — no page-wide form, no
 * confirmation dialog. Used by UnitDetailPage.tsx's and PropertyDetailPage.tsx's Details
 * tabs (AC-D1-AC-D6, docs/features/core-property-crud).
 */

import { useState } from 'react';
import { Pencil, AlertTriangle, Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { joinSelection, splitSelection } from '@/utils/multiselect';

export interface EditableDataRowOption {
  value: string;
  label: string;
}


interface EditableDataRowProps {
  label: string;
  value: string;
  onSave: (newValue: string) => Promise<void>;
  /** 'multiselect' holds a comma-joined list of option values (empty string = none selected). */
  type?: 'text' | 'number' | 'select' | 'multiselect' | 'yesno';
  options?: EditableDataRowOption[];
  placeholder?: string;
  defaultExpanded?: boolean;
  warning?: boolean;
  /** Why this field is being asked for (e.g. which disclosure it drives). Always shown via a
   * "?" tooltip next to the label; also shown inline, unhidden, while unanswered (`warning`)
   * or being edited — the two moments a landlord most needs the explanation without a click. */
  description?: string;
  'data-testid'?: string;
}

const controlButtonClass =
  'min-h-[44px] px-3 inline-flex items-center justify-center rounded-md border border-border text-xs font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

const iconButtonClass =
  'min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

export function EditableDataRow({
  label,
  value,
  onSave,
  type = 'text',
  options,
  placeholder,
  defaultExpanded = false,
  warning = false,
  description,
  'data-testid': testId,
}: EditableDataRowProps) {
  const [isEditing, setIsEditing] = useState(defaultExpanded);
  const [draft, setDraft] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const beginEdit = () => {
    setDraft(value);
    setError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft(value);
    setError(null);
    setIsEditing(false);
  };

  const confirmEdit = async (newValue: string) => {
    try {
      setIsSaving(true);
      setError(null);
      await onSave(newValue);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass =
    'flex-1 min-w-0 px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring box-border';

  // `value` is always the raw underlying value (so it correctly pre-fills the `<select>`
  // when editing starts) — for a 'select' field, resolve it to its option's label for the
  // read-only display text.
  const displayValue =
    type === 'select'
      ? (options?.find((o) => o.value === value)?.label ?? value)
      : type === 'multiselect'
      ? splitSelection(value).map((v) => options?.find((o) => o.value === v)?.label ?? v).join(', ')
      : value;

  return (
    <div className="py-1" data-testid={testId}>
      <dt className="text-xs text-muted-foreground md:text-sm flex items-center gap-1">
        {warning && (
          <AlertTriangle
            aria-hidden="true"
            data-testid={testId ? `${testId}-warning-icon` : undefined}
            className="h-3 w-3 text-warning shrink-0"
          />
        )}
        <span className="inline-flex items-center gap-1">
          {label}
          {description && <HelpTooltip text={description} size="sm" />}
        </span>
      </dt>
      {description && (warning || isEditing) && (
        <p
          className="mt-0.5 text-xs text-muted-foreground"
          data-testid={testId ? `${testId}-description` : undefined}
        >
          {description}
        </p>
      )}
      <dd className="mt-0.5 text-sm font-medium md:text-base text-foreground">
        {isEditing ? (
          <div className="flex flex-col gap-1.5">
            {type === 'yesno' ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => void confirmEdit('true')}
                  aria-label={`${label}: Yes`}
                  data-testid={testId ? `${testId}-yes-button` : undefined}
                  className={controlButtonClass}
                >
                  Yes
                </button>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => void confirmEdit('false')}
                  aria-label={`${label}: No`}
                  data-testid={testId ? `${testId}-no-button` : undefined}
                  className={controlButtonClass}
                >
                  No
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                {type === 'multiselect' ? (
                  <div
                    role="group"
                    aria-label={label}
                    data-testid={testId ? `${testId}-input` : undefined}
                    className="flex w-full flex-wrap gap-x-4"
                  >
                    {options?.map((opt) => {
                      const checked = splitSelection(draft).includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className="inline-flex min-h-[44px] items-center gap-2 text-sm font-normal"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isSaving}
                            onChange={() =>
                              setDraft(
                                joinSelection(
                                  (options ?? [])
                                    .map((o) => o.value)
                                    .filter((v) => (v === opt.value ? !checked : splitSelection(draft).includes(v))),
                                ),
                              )
                            }
                            data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                            className="h-4 w-4"
                          />
                          {opt.label}
                        </label>
                      );
                    })}
                  </div>
                ) : type === 'select' ? (
                  <select
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={isSaving}
                    aria-label={label}
                    data-testid={testId ? `${testId}-input` : undefined}
                    className={cn(inputClass, 'bg-card')}
                  >
                    {options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    autoFocus
                    type={type === 'number' ? 'number' : 'text'}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={placeholder}
                    disabled={isSaving}
                    aria-label={label}
                    data-testid={testId ? `${testId}-input` : undefined}
                    className={inputClass}
                  />
                )}
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => void confirmEdit(draft)}
                  aria-label={`Confirm ${label}`}
                  data-testid={testId ? `${testId}-confirm-button` : undefined}
                  className={cn(
                    iconButtonClass,
                    'bg-primary text-primary-foreground hover:bg-primary/90 border-primary',
                  )}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={cancelEdit}
                  aria-label={`Cancel editing ${label}`}
                  data-testid={testId ? `${testId}-cancel-button` : undefined}
                  className={iconButtonClass}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}
            {error && (
              <p className="text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1">
            <span>{displayValue || '—'}</span>
            <button
              type="button"
              onClick={beginEdit}
              aria-label={`Edit ${label}`}
              data-testid={testId ? `${testId}-edit-button` : undefined}
              className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] -my-2.5 text-muted-foreground hover:text-foreground rounded transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </dd>
    </div>
  );
}

export default EditableDataRow;
